import { useEffect } from 'react'

import {
  clearMobileViewportMetrics,
  scheduleDocumentScrollReset,
  syncMobileViewportMetrics,
} from './mobileViewportMetrics'

export type MobileScrollLockVariant = 'platform' | 'nutrimatic'

const LOCK_CLASS: Record<MobileScrollLockVariant, string> = {
  platform: 'platform-mobile-shell-active',
  nutrimatic: 'nutrimatic-mobile-active',
}

const KEYBOARD_SETTLE_MS = 320

function canScrollElement(element: HTMLElement) {
  return element.scrollHeight > element.clientHeight + 1
}

function handleLockedTouchMove(event: TouchEvent, variant: MobileScrollLockVariant) {
  const target = event.target
  if (!(target instanceof Element)) return

  const scrollContainer = target.closest('[data-mobile-scroll-container]')
  if (scrollContainer instanceof HTMLElement) {
    if (canScrollElement(scrollContainer)) return
    event.preventDefault()
    return
  }

  if (variant === 'nutrimatic' && target.closest('.nm-mobile-simple__token-sheet-root')) {
    event.preventDefault()
    return
  }

  if (variant === 'platform' && target.closest('.mobile-app__sheet-root')) {
    event.preventDefault()
    return
  }

  event.preventDefault()
}

/** Bloqueo documental compartido: platform browse shell y Nutrimatic operativo. */
export function useMobileDocumentScrollLock(active = true, variant: MobileScrollLockVariant = 'platform') {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined

    const root = document.documentElement
    const lockClass = LOCK_CLASS[variant]
    root.classList.add(lockClass)

    const viewport = window.visualViewport
    let keyboardSettleTimer: number | undefined

    const syncMetrics = () => {
      syncMobileViewportMetrics(root)
    }

    const scheduleMetricsAfterKeyboard = () => {
      window.clearTimeout(keyboardSettleTimer)
      keyboardSettleTimer = window.setTimeout(() => {
        syncMetrics()
        scheduleDocumentScrollReset()
      }, KEYBOARD_SETTLE_MS)
    }

    const onTouchMove = (event: TouchEvent) => {
      handleLockedTouchMove(event, variant)
    }

    const onVisualViewportResize = () => syncMetrics()
    const onLayoutChange = () => {
      syncMetrics()
      scheduleDocumentScrollReset()
    }
    const onFocusIn = () => {
      window.clearTimeout(keyboardSettleTimer)
      syncMetrics()
    }
    const onFocusOut = () => {
      syncMetrics()
      scheduleMetricsAfterKeyboard()
    }
    const onWindowScroll = () => scheduleDocumentScrollReset()

    syncMetrics()
    scheduleDocumentScrollReset()

    viewport?.addEventListener('resize', onVisualViewportResize)
    window.addEventListener('resize', onLayoutChange)
    window.addEventListener('scroll', onWindowScroll, { passive: true })
    window.addEventListener('orientationchange', onLayoutChange)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    document.addEventListener('touchmove', onTouchMove, { passive: false })

    return () => {
      window.clearTimeout(keyboardSettleTimer)
      root.classList.remove(lockClass)
      clearMobileViewportMetrics(root)

      viewport?.removeEventListener('resize', onVisualViewportResize)
      window.removeEventListener('resize', onLayoutChange)
      window.removeEventListener('scroll', onWindowScroll)
      window.removeEventListener('orientationchange', onLayoutChange)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      document.removeEventListener('touchmove', onTouchMove)

      scheduleDocumentScrollReset()
      window.setTimeout(scheduleDocumentScrollReset, KEYBOARD_SETTLE_MS)
    }
  }, [active, variant])
}
